import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Dialog, DialogContent, FormControlLabel, IconButton, Radio, RadioGroup, Typography, } from "@mui/material";
import { X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { fetchInvoicePreview, saveInvoiceNotes, selectSavingInvoiceNotes, } from "../../../redux/billingSummarySlice";
import { selectToken } from "../../../redux/authSlice";
import { ApiCall } from "../../../helper/axios";
import AppButton from "../../../common_components/AppButton";
import CommonLoader from "../../../common_components/CommonLoader";
import CustomLoader from "../../../common_components/CustomLoader";
import { TextArea } from "../../../common_components/FormInputs";
import { toast } from "../../../common_components/Toast/ToastUtils";
import { INVOICE_APPROVAL_STATUSES } from "../billingSummary.constants";
const MODULE_KEY = "billing_summary";
/** Same geometry as the module's other modal buttons — see `GenerateInvoiceModal`'s note
 *  on why `color="inherit"` is required to escape the global `containedPrimary` rule. */
const BUTTON_SX = {
    minWidth: 110,
    height: 36,
    minHeight: 36,
    px: "14px",
    borderRadius: "8px",
    textTransform: "capitalize",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
    whiteSpace: "nowrap",
    boxShadow: "0px 1px 2px 0px #0A0D120D",
};
/**
 * "Invoice Preview" — the raised invoice's PDF, with the internal approval state and
 * notes beneath it.
 *
 * Self-contained: it loads its own PDF and review state and saves its own notes, because
 * nothing outside it depends on either. `approval_status` and `invoice_notes` aren't
 * listing columns, so there's no re-fetch to trigger on save.
 *
 * `readOnly` drops the review half and shows the PDF alone — see the prop's note.
 */
const InvoicePreviewModal = ({ open, invoiceId, invoiceNo, clientName, onClose, readOnly = false, }) => {
    const dispatch = useDispatch();
    const token = useSelector(selectToken);
    const saving = useSelector(selectSavingInvoiceNotes);
    const [loading, setLoading] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [approvalStatus, setApprovalStatus] = useState("");
    const [notes, setNotes] = useState("");
    /**
     * Load the review state and the PDF each time a different invoice is opened.
     *
     * The PDF route sits behind `authMiddleware`, so it can't be an `<iframe src>` — the
     * browser wouldn't attach the bearer token. It's fetched as a blob and rendered from an
     * object URL, which the cleanup revokes so closing the modal doesn't leak it.
     *
     * The two requests are reported separately on purpose: a preview-state read that fails
     * still leaves a viewable PDF, so only a PDF failure becomes the error state.
     */
    useEffect(() => {
        if (!open || !invoiceId)
            return;
        let cancelled = false;
        let objectUrl = null;
        setLoading(true);
        setLoadError(null);
        setPdfUrl(null);
        setApprovalStatus("");
        setNotes("");
        (async () => {
            // Skipped in view-only mode: there is no review section to fill, and the
            // endpoint 404s on an invoice that isn't the one recorded on a billing row.
            if (!readOnly) {
                try {
                    const preview = await dispatch(fetchInvoicePreview(invoiceId)).unwrap();
                    if (cancelled)
                        return;
                    setApprovalStatus(preview.approval_status || "");
                    setNotes(preview.invoice_notes || "");
                }
                catch {
                    // Left at their empty defaults — the reviewer can still set them.
                }
            }
            try {
                const response = await ApiCall("GET", `/api/billing-summary/invoice-pdf/${encodeURIComponent(invoiceId)}`, {}, { Authorization: token || "", "x-module-key": MODULE_KEY }, 2 * 60 * 1000, "blob");
                if (!response || response.status !== 200 || !response.data) {
                    throw new Error("Invoice PDF request failed");
                }
                objectUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
                // Closed (or switched invoice) while the request was in flight — the URL
                // this call created still has to be released, since cleanup already ran.
                if (cancelled) {
                    window.URL.revokeObjectURL(objectUrl);
                    objectUrl = null;
                    return;
                }
                setPdfUrl(objectUrl);
            }
            catch (err) {
                console.error("Invoice PDF load error:", err);
                if (!cancelled) {
                    setLoadError("Couldn't load this invoice's PDF from QuickBooks. Please try again.");
                }
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            if (objectUrl)
                window.URL.revokeObjectURL(objectUrl);
        };
    }, [open, invoiceId, dispatch, token, readOnly]);
    /**
     * Both fields go in one request. The endpoint stores an omitted field as null rather
     * than leaving it alone, so sending only the one that changed would wipe the other —
     * hence the empty string being sent as an explicit null.
     */
    const handleSave = useCallback(async () => {
        try {
            await dispatch(saveInvoiceNotes({
                invoice_id: invoiceId,
                approval_status: approvalStatus || null,
                notes: notes.trim() || null,
            })).unwrap();
            toast.success("Internal status and notes saved.");
            onClose();
        }
        catch (err) {
            toast.error(typeof err === "string"
                ? err
                : err?.message || "Failed to save the internal status and notes. Please try again.");
        }
    }, [dispatch, invoiceId, approvalStatus, notes, onClose]);
    if (!open)
        return null;
    return (_jsx(Dialog, { open: open, maxWidth: "lg", fullWidth: true, 
        // Consistent with the module's other dialogs: only the explicit controls dismiss
        // it, so a stray click can't discard notes mid-write.
        disableEscapeKeyDown: true, sx: {
            zIndex: 2000,
            "& .MuiBackdrop-root": {
                backgroundColor: "rgba(0,0,0,0.5)",
                backdropFilter: "blur(3px)",
            },
            "& .MuiDialog-container > .MuiPaper-root": { borderRadius: "12px", maxWidth: "1100px" },
        }, children: _jsxs(DialogContent, { sx: { p: 0 }, children: [_jsxs(Box, { sx: {
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 2,
                        px: 3,
                        py: 2,
                        borderBottom: "1px solid #EAECF0",
                    }, children: [_jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: "18px", fontWeight: 600, color: "#101828", lineHeight: "28px" }, children: "Invoice Preview" }), (clientName || invoiceNo) && (_jsx(Typography, { sx: { fontSize: "14px", color: "#667085", lineHeight: "20px", mt: 0.25 }, children: [clientName, invoiceNo ? `Invoice no. ${invoiceNo}` : ""].filter(Boolean).join(" - ") }))] }), _jsx(IconButton, { onClick: onClose, disabled: saving, sx: { width: 36, height: 36, color: "#98A2B3", "&:hover": { color: "#475467", bgcolor: "#F9FAFB" } }, children: _jsx(X, { size: 20 }) })] }), _jsx(Box, { sx: {
                        height: "60vh",
                        minHeight: 360,
                        bgcolor: "#F9FAFB",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }, children: loading ? (_jsx(CommonLoader, { className: "table-loader" })) : loadError ? (_jsx(Box, { sx: { px: 3, width: "100%", maxWidth: 520 }, children: _jsx(Alert, { severity: "error", sx: { borderRadius: "10px", alignItems: "center", fontSize: "13px" }, children: loadError }) })) : pdfUrl ? (_jsx(Box, { component: "iframe", src: pdfUrl, title: "Invoice PDF", sx: { width: "100%", height: "100%", border: "none", display: "block" } })) : (_jsx(Typography, { sx: { fontSize: "14px", color: "#667085" }, children: "No invoice PDF available." })) }), !readOnly && (_jsxs(Box, { sx: { px: 3, py: 2, borderTop: "1px solid #EAECF0" }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }, children: [_jsx(Typography, { sx: { fontSize: "14px", fontWeight: 500, color: "#344054", whiteSpace: "nowrap" }, children: "Internal Status:" }), _jsx(RadioGroup, { row: true, value: approvalStatus, onChange: (event) => setApprovalStatus(event.target.value), children: INVOICE_APPROVAL_STATUSES.map((option) => (_jsx(FormControlLabel, { value: option.value, label: option.label, disabled: saving, control: _jsx(Radio, { sx: { color: "#FF008E", "&.Mui-checked": { color: "#FF008E" } } }), sx: { "& .MuiFormControlLabel-label": { fontSize: "14px", color: "#344054" } } }, option.value))) })] }), _jsx(Box, { sx: { mt: 1 }, children: _jsx(TextArea, { label: "Notes", value: notes, onChange: (event) => setNotes(event.target.value), placeholder: "Add internal notes about this invoice...", minRows: 3, size: "sm", disabled: saving }) })] })), _jsxs(Box, { sx: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 1.5,
                        flexWrap: "wrap",
                        px: 3,
                        py: 2,
                        borderTop: "1px solid #EAECF0",
                    }, children: [_jsx(AppButton, { variant: "contained", color: "inherit", disabled: saving, onClick: onClose, sx: {
                                ...BUTTON_SX,
                                bgcolor: "#fff",
                                color: "#414651",
                                border: "1px solid #D5D7DA",
                                "&:hover": { bgcolor: "#F9FAFB", border: "1px solid #D5D7DA" },
                            }, children: readOnly ? "Close" : "Cancel" }), !readOnly && (_jsx(AppButton, { variant: "contained", color: "inherit", disabled: saving, onClick: handleSave, sx: {
                                ...BUTTON_SX,
                                bgcolor: "#181D27",
                                color: "#fff",
                                border: "1px solid #181D27",
                                "&:hover": { bgcolor: "#101828", border: "1px solid #101828" },
                            }, children: saving ? _jsx(CustomLoader, { color: "inherit", size: 18 }) : "Save" }))] })] }) }));
};
export default InvoicePreviewModal;
